import {DELAY_MINUTE_OPTIONS} from '@/features/trip-delay/tripDelayReasons'
import {formatDelayTime} from '@/features/trip-delay/formatDelayTime'

/**
 * THE DELAY FLOW'S PARTS — the bubbles, chips and banner the chat feed is built from.
 *
 * ── ⚠️ A DELAY IS NOT A CHAT MESSAGE, AND NONE OF THIS IS SENT ────────────────────
 * The server stores a delay as a `requested_delay` row on the trip. It stores no messages
 * for it. Everything below is drawn by the client from that row and rendered AS a
 * conversation, because that is how the driver's app renders the same row — the two sides
 * show the same scripted exchange, mirrored.
 *
 * The consequence worth remembering: the driver never "said" any of this. `Okay, keep us
 * updated` is the product speaking in the driver's voice. Do not add anything to this
 * script that a driver could be held to.
 *
 * ── ⚠️ THE CHIPS ARE DRAWN TWICE, LIVE AND DEAD ───────────────────────────────────
 * The same four chips are a control when a delay is being reported and a read-only record
 * of what was chosen afterwards. One component, a `disabled` prop, so the history cannot
 * drift from the live row it is a picture of.
 */

/* ── One bubble ─────────────────────────────────────────────────────────────────────
   Matches TripChat's own bubble geometry deliberately: these sit in the same column as
   real messages, and a different radius or padding would read as a different product
   rather than as part of the thread. */
function Bubble({mine, children, time, face}) {
    return (
        <div className={`flex items-end gap-2 ${mine ? 'justify-end' : 'justify-start'}`}>
            {!mine && face}
            <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                mine ? 'bg-(--bg-dark) text-white' : 'bg-(--bg-grey) text-(--text-primary)'
            }`}>
                <p className="mb-0! whitespace-pre-wrap">{children}</p>
                {time && (
                    <span className={`mt-1 block text-right text-[11px] ${mine ? 'text-white/60' : 'text-(--text-secondary)'}`}>
                        {time}
                    </span>
                )}
            </div>
            {mine && face}
        </div>
    )
}

/**
 * One minutes chip.
 *
 * ⚠️ TAPPING SUBMITS. There is no confirm step and no selected-then-send: the chip IS the
 * send. So while one is in flight ALL of them disable, not just the one pressed — a second
 * tap on a different value would be a second delay request racing the first, and the server
 * answers the loser with `requested_delay_active`.
 */
export function DelayMinutesChip({label, selected, disabled, busy, onClick}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`min-w-16 cursor-pointer rounded-full border px-4 py-2 text-sm font-semibold transition-opacity disabled:cursor-default ${
                selected
                    ? 'border-transparent bg-(--bg-dark) text-white'
                    : 'border-(--border-color) bg-(--bg-white) text-(--text-primary)'
            } ${disabled && !selected ? 'opacity-40' : ''}`}
        >
            {/* The spinner replaces the label rather than sitting beside it, so the chip
                does not change width mid-request and shuffle the row under the finger. */}
            {busy ? <Spinner/> : label}
        </button>
    )
}

function Spinner() {
    return (
        <span
            className="mx-auto block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
            aria-label="Sending"
            role="status"
        />
    )
}

/** The four chips as a row. `value` marks one selected; `onPick` absent means read-only. */
export function DelayMinutesRow({value, onPick, submitting = null, disabled = false}) {
    return (
        <div className="flex flex-wrap gap-2 py-1">
            {DELAY_MINUTE_OPTIONS.map((option) => (
                <DelayMinutesChip
                    key={option.minutes}
                    label={option.label}
                    selected={value === option.minutes}
                    busy={submitting === option.minutes}
                    disabled={disabled || !onPick || submitting != null}
                    onClick={onPick ? () => onPick(option.minutes) : undefined}
                />
            ))}
        </div>
    )
}

/**
 * ONE REPORTED DELAY, as the five-part exchange both apps render.
 *
 * ⚠️ THE ORDER IS FIXED AND SO IS THE COPY. The driver's app draws these same five, in
 * this order, mirrored — their side on the right and the passenger's on the left. A line
 * added on one side only would make the two products disagree about what was said.
 *
 * ⚠️ THE TIME IS PARSED IN THE APP ZONE. `requested_at` is Toronto wall clock with no zone
 * on it; the mobile app formats it with a bare `new Date`, so its history times are wrong
 * for anyone outside Eastern. That bug is not ported.
 */
export function DelaySystemBubble({delay, myFace, driverFace}) {
    const time = formatDelayTime(delay.requested_at)

    return (
        <div className="flex flex-col gap-2">
            <Bubble mine time={time} face={myFace}>{delay.reason_text}</Bubble>
            <Bubble mine={false} time={time} face={driverFace}>
                How much more time do you think you need?
            </Bubble>
            {/* Read-only: a record of which chip was pressed, not a chance to change it. */}
            <DelayMinutesRow value={delay.minutes} disabled/>
            <Bubble mine time={time} face={myFace}>{`${delay.minutes} min`}</Bubble>
            <Bubble mine={false} time={time} face={driverFace}>Okay, keep us updated</Bubble>
        </div>
    )
}

/**
 * THE COUNTDOWN, while a window is open.
 *
 * ⚠️ AMBER, NOT RED. Nothing has gone wrong — the passenger told their driver they would be
 * late and the driver agreed to wait. Red would read as a failure state on a flow working
 * exactly as designed.
 */
export function DelayWarningBanner({reasonText, label}) {
    return (
        <div className="flex items-center gap-2 rounded-(--radius-lg) bg-[#FFF4E0] px-4 py-3 text-sm font-semibold text-[#8A5A00]">
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9"/>
                <path d="M12 7v5.5l3.5 2"/>
            </svg>
            <span>{reasonText} · {label} remaining</span>
        </div>
    )
}

/**
 * THE ENTRY CARD, in the feed.
 *
 * ⚠️ IT LIVES IN THE THREAD RATHER THAN IN THE HEADER because it is the opening line of a
 * conversation, not a screen-level action: the driver-side "What's holding you up?" above
 * it is what makes the card an answer rather than a button.
 */
export function DelayEntryCard({onOpen, driverFace, showQuestion = true}) {
    return (
        <div className="flex flex-col gap-2">
            {/* ⚠️ ONLY ON THE FIRST OFFER. Re-offered under an earlier delay's exchange, the
                question landed AFTER "Okay, keep us updated" and read as the thread running
                upside down — there the card alone is the invitation. */}
            {showQuestion && (
                <div className="flex items-end gap-2">
                    {driverFace}
                    <div className="max-w-[75%] rounded-2xl bg-(--bg-grey) px-3 py-2 text-sm text-(--text-primary)">
                        <p className="mb-0!">What&apos;s holding you up?</p>
                    </div>
                </div>
            )}
            <button
                type="button"
                onClick={onOpen}
                className="flex w-full cursor-pointer items-center gap-3 rounded-(--radius-lg) border border-(--border-color) bg-(--bg-white) p-4 text-left hover:opacity-80 max-md:gap-2 max-md:p-3"
            >
                <span className="min-w-0 flex-1">
                    <span className="block font-bold text-(--text-primary) max-md:text-sm">Experiencing a Delay?</span>
                    <span className="block text-(--text-secondary) max-md:text-xs">
                        Immigration, customs, or lost baggage, let your driver know.
                    </span>
                </span>
                <svg className="h-4 w-4 shrink-0 text-(--text-secondary)" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m9 6 6 6-6 6"/>
                </svg>
            </button>
        </div>
    )
}

/** The same slot, while the landing check is still resolving. */
export function DelayEntrySkeleton() {
    return (
        <div className="flex flex-col gap-2" aria-label="Loading delay options" role="status">
            <div className="h-9 w-40 animate-pulse rounded-2xl bg-(--bg-grey)"/>
            <div className="h-20 w-full animate-pulse rounded-(--radius-lg) bg-(--bg-grey)"/>
        </div>
    )
}

/**
 * MEET YOUR DRIVER — pinned to the bottom of the thread, above the composer.
 *
 * ⚠️ IT IS NOT PART OF THE DELAY FLOW; it shares the feed. A passenger who opened this
 * thread to say they are running late is one tap from the walk that follows, and a
 * passenger who opened it for any other reason still has the same walk ahead of them.
 *
 * ⚠️ PINNED LAST, WHICH IS WHY IT IS NOT A MESSAGE. Every real item in the feed is
 * placed by its timestamp; this one is placed by being the thing you do NEXT, so it sits
 * under the conversation however old or new that conversation is.
 *
 * ⚠️ AND IT DISAPPEARS ONCE THEY ARE THERE. Fifty metres from the booth the walk is
 * over, and a card offering to start it is telling someone standing at the kerb to set off.
 */
export function MeetYourDriverCard({walkSummary, pickupLabel, onStartWalking}) {
    return (
        <div className="flex flex-col gap-2 rounded-(--radius-lg) border border-(--border-color) bg-(--bg-white) p-4">
            <span className="block text-sm font-bold text-(--text-primary)">Meet Your Driver</span>
            {walkSummary && (
                <span className="block text-xs text-(--text-secondary)">{walkSummary}</span>
            )}
            <button
                type="button"
                onClick={onStartWalking}
                className="mt-1 min-h-11 w-full cursor-pointer rounded-full border-0 bg-(--bg-dark) px-4 text-sm font-semibold text-white"
            >
                Start Walking to Pickup Area
            </button>
            {pickupLabel && (
                <span className="block text-xs text-(--text-secondary)">{pickupLabel}</span>
            )}
        </div>
    )
}
